import useSWR from "swr";
import { Facebook, Instagram, Mail, MapPin, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { WEEKDAYS_IT } from "@/lib/format";
import { useSettings } from "@/context/SettingsContext";
import Reveal from "@/components/site/Reveal";
import Seo from "@/components/site/Seo";
import PageHeader from "@/components/site/PageHeader";

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function Contatti() {
  const { settings } = useSettings();
  const { data: hours } = useSWR("/opening-hours", fetcher, { revalidateOnFocus: false });

  return (
    <>
      <Seo
        title="Contatti · Osteria Aterrana, Via Madonna di Montevergine 34, Montoro (AV)"
        description="Contatti e orari dell'Osteria Aterrana: Via Madonna di Montevergine 34, borgo storico di Aterrana, Montoro (AV). Telefono 331 10 91 971."
        path="/contatti"
      />
      <PageHeader
        eyebrow="Contatti"
        title="Dove trovarci"
        lead="Siamo dentro il borgo: l'ultimo tratto si fa a piedi. Per qualsiasi cosa, una telefonata è la via più rapida."
        label="BORGO PHOTO"
        alt="La facciata in pietra dell'osteria nel borgo di Aterrana"
        src={settings.contact_header_image}
      />

      <section className="section-y bg-cream">
        <div className="container-ed grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5 space-y-10">
            <Reveal>
              <p className="eyebrow text-sage">Indirizzo</p>
              <address className="mt-4 not-italic font-serif text-2xl md:text-3xl leading-snug text-wood">
                Osteria Aterrana
                <br />
                {settings.address || "Via Madonna di Montevergine 34"}
                <br />
                <span className="text-xl md:text-2xl text-wood/70">
                  {settings.address_line2 || "Borgo storico di Aterrana · Montoro (AV)"}
                </span>
              </address>
              <p className="mt-3 text-xs tracking-wider text-foreground/50" data-testid="contact-vat">
                P.IVA 03083930648
              </p>
            </Reveal>

            <Reveal delay={0.08}>
              <p className="eyebrow text-sage">Telefono ed email</p>
              <a
                href={`tel:${settings.phone_link || "+393311091971"}`}
                data-testid="contact-phone"
                className="mt-3 inline-flex items-center gap-3 font-serif text-3xl md:text-4xl text-wood hover:text-sage transition-colors duration-400"
              >
                <Phone size={22} strokeWidth={1.3} /> {settings.phone || "331 10 91 971"}
              </a>
              <a
                href={`mailto:${settings.email || "osteriaaterrana@outlook.it"}`}
                data-testid="contact-email"
                className="mt-4 flex items-center gap-3 text-base text-sage link-underline w-fit break-all"
              >
                <Mail size={18} strokeWidth={1.4} /> {settings.email || "osteriaaterrana@outlook.it"}
              </a>
              <p className="mt-3 text-sm text-foreground/55">
                Tocca per chiamare. Per gruppi numerosi o richieste particolari, preferiamo parlarne.
              </p>
            </Reveal>

            <Reveal delay={0.14}>
              <p className="eyebrow text-sage">Orari</p>
              <ul className="mt-4 divide-y divide-wood/12 border-t border-wood/12" data-testid="opening-hours">
                {WEEKDAYS_IT.map((day, idx) => {
                  const key = String(idx);
                  const services = hours?.[key] || [];
                  return (
                    <li key={day} className="flex items-baseline justify-between gap-6 py-3">
                      <span className="text-sm text-wood/80">{day}</span>
                      <span className="text-sm text-right text-foreground/65">
                        {services.length === 0
                          ? "Chiuso"
                          : services
                              .map((s) => `${s.name} ${s.start_time}–${s.end_time}`)
                              .join(" · ")}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Reveal>

            <Reveal delay={0.2} className="flex flex-wrap gap-3">
              <a
                href={settings.maps_url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-maps"
                className="inline-flex items-center gap-2 px-7 py-4 bg-wood text-cream text-[0.7rem] uppercase tracking-[0.2em] hover:bg-sage transition-colors duration-500"
              >
                <MapPin size={14} /> Indicazioni
              </a>
              <a
                href={settings.instagram || "https://www.instagram.com/osteria_aterrana/"}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram"
                className="inline-flex items-center gap-2 px-7 py-4 border border-wood/30 text-wood text-[0.7rem] uppercase tracking-[0.2em] hover:bg-wood hover:text-cream transition-colors duration-500"
              >
                <Instagram size={14} /> Instagram
              </a>
              {settings.facebook && (
                <a
                  href={settings.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-facebook"
                  className="inline-flex items-center gap-2 px-7 py-4 border border-wood/30 text-wood text-[0.7rem] uppercase tracking-[0.2em] hover:bg-wood hover:text-cream transition-colors duration-500"
                >
                  <Facebook size={14} /> Facebook
                </a>
              )}
              <Link
                to="/prenota"
                data-testid="contact-book"
                className="inline-flex items-center gap-2 px-7 py-4 bg-sage text-cream text-[0.7rem] uppercase tracking-[0.2em] hover:bg-wood transition-colors duration-500"
              >
                Prenota un tavolo
              </Link>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="lg:col-span-6 lg:col-start-7">
            <div className="border border-wood/20">
              <iframe
                title="Mappa dell'Osteria Aterrana"
                src="https://www.openstreetmap.org/export/embed.html?bbox=14.75%2C40.79%2C14.83%2C40.85&amp;layer=mapnik"
                className="w-full h-[380px] md:h-[560px] grayscale-[30%]"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
